import React, {useEffect, useRef} from 'react';
import styled from 'styled-components';
import ReactPlayer from 'react-player';
import { useSwipeable } from "react-swipeable";

import useLockBodyScroll from 'hooks/useLockBodyScroll';

import Icon from 'components/icons/icon';

import theme, { bp, color } from 'settings/theme';

// import { FoldingCube } from 'better-react-spinkit';


// Component
const Component = (props) => {

  // Make sure references are always up to date to the previous and next callbacks
  // Explanation here - https://overreacted.io/making-setinterval-declarative-with-react-hooks/
  const savedMovePrevRequest = useRef<Function>();
  const savedMoveNextRequest = useRef<Function>();

  useEffect(() => {
    savedMovePrevRequest.current = props.onMovePrevRequest;
    savedMoveNextRequest.current = props.onMoveNextRequest;
  }, [props.onMovePrevRequest, props.onMoveNextRequest]);


  // Stop body from scrolling underneath modal
  useLockBodyScroll();

  // Handle Key Presses
  const handleLeftRightKeys = (e) => {
      
    switch (e.keyCode) {
      case 37:
        savedMovePrevRequest.current!();
        break;

      case 39:
        savedMoveNextRequest.current!()
        break;

      case 27:
        props.onCloseRequest()
        break;
  
      default:
        break;
    }

  }

  useEffect(() => {

    window.document.addEventListener('keydown', handleLeftRightKeys);

    return () => {
      window.document.removeEventListener('keydown', handleLeftRightKeys);
    };

  }, []);
  

  // Swipe Handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => savedMoveNextRequest.current!(),
    onSwipedRight: () => savedMovePrevRequest.current!(),
    trackMouse: true
  })


  return (

    <StyledGalleryModal {...props} className="galleryModal" {...swipeHandlers}>

      <div className="galleryModal_backdrop" onClick={props.onCloseRequest}></div>
      <div className="galleryModal_loading">
        {/* <FoldingCube size={80} color='gray' /> */}
      </div>
      <div className="galleryModal_main">

        {/* Images */}
        {props.currentSlide && props.currentSlide.type === 'image' &&
          <div className="galleryModal_image">
            <img src={props.currentSlide.src} title={props.currentSlide.title || 'Photo'} alt={props.currentSlide.alt_text || 'Photo'} />
          </div>
        }

        {/* Youtube Videos */}
        {props.currentSlide && props.currentSlide.type === 'youtube' &&
          <div className="galleryModal_video galleryModal_youtube">
            <div className="galleryModal_video_inner">
              <ReactPlayer 
                width="100%" 
                height="100%" 
                url={`https://www.youtube.com/watch?v=${props.currentSlide.youtubeId}`} 
                playing 
                controls={true}
              />
            </div>
          </div>
        }

        {/* Vimeo Videos */}
        {props.currentSlide && props.currentSlide.type === 'vimeo' &&
          <div className="galleryModal_video galleryModal_vimeo">
            <div className="galleryModal_video_inner">
              <ReactPlayer 
                width="100%" 
                height="100%" 
                url={`https://vimeo.com/${props.currentSlide.vimeoId}`} 
                playing 
                controls={true}
              />
            </div>
          </div>
        }
          
      </div>
      <div className="galleryModal_controls">

        <button onClick={props.onCloseRequest} className="galleryModal_controls_close">
          <Icon iconName="cross-circle" iconTitle="close"></Icon>
        </button>

        <button onClick={props.onMovePrevRequest} className="galleryModal_controls_previous">
          <Icon iconName="chevron" iconDirection="left" />
        </button>

        <button onClick={props.onMoveNextRequest} className="galleryModal_controls_next">
          <Icon iconName="chevron" iconDirection="right" />
        </button>

      </div>

    </StyledGalleryModal>

  );

}

export default Component


const StyledGalleryModal = styled.div`
    
  z-index: 10;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  width: 100vw;
  height: 100vh;
  color: ${color.text};

  .galleryModal_backdrop,
  .galleryModal_loading,
  .galleryModal_main,
  .galleryModal_controls{
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
  }

  .galleryModal_backdrop{
    background-color: ${color.white};
    opacity: 0.9;
  }

  .galleryModal_loading{

    pointer-events: none;

    display: flex;
    align-items: center;
    justify-content: center;
      
  }

  .galleryModal_main{

    pointer-events: none;

    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    img{
      max-width: 100vw;
      max-height: 100vh;
    }
      
  }

  .galleryModal_controls{

    .icon{
      svg{
        fill: white;
      }
    }

  }

  .galleryModal_video{

    pointer-events: all;

    width: 100%;
    max-width: 1200px;
    margin: auto;

    .galleryModal_video_inner{

      position: relative;
      width: 100%;
      padding-top: 56.25%;

      > *{
        position: absolute;
        top: 0;
      }

    }

  }

  .galleryModal_controls{

    z-index: 2;
    pointer-events: none;

    .galleryModal_controls_close{

      pointer-events: all;

      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      right: 8px;
      top: 8px;
      width: 36px;
      height: 36px;
      border-radius: 18px;
      background-color: rgba(255, 255, 255, 0);
      transition: all 0.15s ease-out;
      -webkit-tap-highlight-color: transparent;

      &:hover, &:active{
        background-color: rgba(255, 255, 255, 0.2);
      }

    }

    .galleryModal_controls_previous,
    .galleryModal_controls_next{

      pointer-events: all;

      position: absolute;
      top: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 60px;
      background-color: rgba(0,0,0,0.05);
      -webkit-tap-highlight-color: transparent;
      transform: translateY(-50%);
      transition: all 0.15s ease-out;

      &:hover, &:active{
        background-color: rgba(0,0,0,0.15);
      }

    }

    .galleryModal_controls_previous{
      left: 0;
      border-radius: 0 6px 6px 0;
    }

    .galleryModal_controls_next{
      right: 0;
      border-radius:  6px 0 0 6px;
    }

    .galleryModal_controls_spinner{
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }

      
  }

  @media (min-width: ${bp(3)}) {
      
    .galleryModal_controls{

      .galleryModal_controls_close{

        right: 10px;
        top: 10px;
        width: 50px;
        height: 50px;
        border-radius: 25px;

        .icon{
            width: 30px;
            height: 30px;
        }

      }

    }
      
  }
    

`